import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { loadStripe } from "@stripe/stripe-js";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { attachePaymentMethod, setupOneClickCharge } from "../services/apiService";
import { getConnectorPrice } from "../utils/rateUtils";
import { buildStationPanel } from "../utils/stationPanel";
import { isCardRefusal, refusalReason } from "../utils/cardRefusal";
import analyticsService from "../services/analyticsService";
import { setTokens } from "../services/tokenService";
import ScreenLayout from "./ui/ScreenLayout";
import ConnectorCard from "./ui/ConnectorCard";
import Field from "./ui/Field";
import Button from "./ui/Button";
import Icon from "./ui/Icon";
import HowItWorksSheet from "./payment/HowItWorksSheet";
import CardRefusedScreen from "./payment/CardRefusedScreen";
import InvoiceSheet from "./payment/InvoiceSheet";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const STRIPE_FONTS = [{ cssSrc: "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500&display=swap" }];

const ELEMENT_STYLE = {
  base: {
    color: "#191919",
    fontFamily: "Montserrat, sans-serif",
    fontSize: "16px",
    "::placeholder": { color: "#A1A1AA" },
  },
  invalid: { color: "#C40505" },
};

const CARD_BRANDS = ["CB", "VISA", "MC", "AMEX"];

const CardBrands = () => (
  <span className="flex gap-1" aria-hidden="true">
    {CARD_BRANDS.map((brand) => (
      <span
        key={brand}
        className="h-5 w-8 rounded border border-dashed border-[#A1A1AA] flex items-center justify-center text-[8px] font-bold text-[#71717A]"
      >
        {brand}
      </span>
    ))}
  </span>
);

// Exécute reCAPTCHA v3 avec l'action donnée.
const executeRecaptcha = (action) =>
  new Promise((resolve, reject) => {
    if (!window.grecaptcha) {
      reject(new Error("reCAPTCHA n'est pas chargé"));
      return;
    }
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, { action })
        .then(resolve)
        .catch(reject);
    });
  });

// Écran 1c : paiement par carte. La préautorisation acquise, la facture
// nominative (1m) s'ouvre par-dessus ; un refus de la banque mène à 1g.
const CheckoutForm = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { connector, evcpData, email } = location.state || {};
  const stripe = useStripe();
  const elements = useElements();

  const [isProcessing, setIsProcessing] = useState(false);
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [complete, setComplete] = useState({ number: false, expiry: false, cvc: false });
  const [refusal, setRefusal] = useState(null);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [authorization, setAuthorization] = useState(null);

  const analyticsParams = { station_id: evcpData?.reference, connector_id: connector?.reference };

  // Sans e-mail (accès direct à l'URL), on repasse par l'écran 1b.
  useEffect(() => {
    if (!email) navigate("/email", { replace: true, state: location.state });
  }, [email]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    analyticsService.pushEvent("payment_step_viewed", analyticsParams);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Charger le script reCAPTCHA v3
  useEffect(() => {
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const onFieldChange = (field) => (event) => {
    setComplete((current) => ({ ...current, [field]: event.complete }));
    setFieldErrors((current) => ({ ...current, [field]: event.error?.message || "" }));
  };

  const refuse = (error) => {
    const reason = refusalReason(error);
    analyticsService.pushEvent("preauth_failed", {
      ...analyticsParams,
      error_type: reason,
      decline_code: error?.decline_code || error?.code || error?.data?.code,
    });
    setRefusal(reason);
  };

  const handleSubmit = async () => {
    if (isProcessing) return;
    setFormError("");

    // Préautorisation déjà acquise : il ne reste qu'à répondre pour la facture.
    if (authorization) {
      setInvoiceOpen(true);
      return;
    }

    if (!stripe || !elements) {
      setFormError(t("errors.unknownError"));
      return;
    }

    setIsProcessing(true);
    try {
      const recaptchaToken = await executeRecaptcha("payment");
      if (!recaptchaToken) throw new Error("La vérification reCAPTCHA a échoué");

      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardNumberElement),
        billing_details: { email },
      });
      if (paymentMethodError) {
        if (isCardRefusal(paymentMethodError)) refuse(paymentMethodError);
        else {
          console.error("[Stripe error]", paymentMethodError);
          setFormError(t("payment.techError"));
        }
        return;
      }

      // Client invité : { accessToken, refreshToken }
      const setupData = await setupOneClickCharge(email);
      setTokens(setupData);

      // Rattache la carte et crée (ou retrouve) la préautorisation de 40 €.
      const attachData = await attachePaymentMethod(paymentMethod.id, setupData.accessToken);

      // Confirmer uniquement si le PI n'est pas déjà pré-autorisé
      if (attachData.status !== "requires_capture") {
        if (!attachData.clientSecret) throw new Error(t("errors.unknownError"));
        const { error: confirmError } = await stripe.confirmCardPayment(attachData.clientSecret, {
          payment_method: paymentMethod.id,
        });
        if (confirmError) {
          if (isCardRefusal(confirmError)) refuse(confirmError);
          else {
            console.error("[Stripe confirm error]", confirmError);
            setFormError(t("payment.techError"));
          }
          return;
        }
      }

      analyticsService.pushEvent("preauth_confirmed", analyticsParams);
      setAuthorization({ setupData, paymentMethod, intentId: attachData.intentId });
      setInvoiceOpen(true);
      analyticsService.pushEvent("invoice_form_opened", { ...analyticsParams, context: "payment" });
    } catch (error) {
      // Carte refusée par la banque dès son rattachement (402 de l'API).
      if (isCardRefusal(error)) refuse(error);
      else {
        console.error("Erreur lors du processus de paiement:", error);
        setFormError(t("payment.techError"));
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const goToCharging = (invoiceDetails = null) => {
    navigate("/start-charging", {
      state: {
        paymentMethodId: authorization.paymentMethod.id,
        accessToken: authorization.setupData.accessToken,
        connector,
        evcpData,
        paymentIntentId: authorization.intentId,
        email,
        invoiceDetails,
        requestStart: true,
      },
    });
  };

  const retryWithAnotherCard = () => {
    setRefusal(null);
    [CardNumberElement, CardExpiryElement, CardCvcElement].forEach((Element) =>
      elements?.getElement(Element)?.clear()
    );
  };

  const cardComplete = complete.number && complete.expiry && complete.cvc;

  return (
    <>
      {refusal && (
        <CardRefusedScreen
          reason={refusal}
          station={evcpData?.reference}
          connector={connector?.reference}
          onRetry={retryWithAnotherCard}
          onBack={retryWithAnotherCard}
          panel={buildStationPanel({ t, language: i18n.language, variant: "summary", evcpData, connector })}
        />
      )}

      {/* Le formulaire reste monté sous l'écran de refus : les champs Stripe
          doivent exister pour être vidés puis ressaisis. */}
      <div hidden={Boolean(refusal)}>
        <ScreenLayout
          onBack={() => navigate(-1)}
          step={3}
          panel={buildStationPanel({ t, language: i18n.language, variant: "summary", evcpData, connector })}
          footer={
            <>
              <Button
                icon="lock-bold"
                onClick={handleSubmit}
                loading={isProcessing}
                disabled={!stripe || (!authorization && !cardComplete)}
              >
                {isProcessing ? t("payment.processing") : t("payment.submit")}
              </Button>
            </>
          }
        >
          <h1 className="mb-5 text-[28px] leading-9 tracking-[-.02em] font-bold md:text-[32px] md:leading-10">
            {t("payment.title")}
          </h1>

          <ConnectorCard connector={connector} price={getConnectorPrice(connector, evcpData)} />

          <div className="mt-3 rounded-xl bg-qv-brand-50 px-4 py-3.5 flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
            <div className="flex gap-2.5 items-start md:flex-1 md:items-center">
              <Icon name="shield-check-linear" size={20} className="text-qv-brand" />
              <p className="text-sm leading-5 font-medium">
                <Trans i18nKey="payment.preauth" components={[<b />, <b />]} />
              </p>
            </div>
            <Button
              variant="secondary"
              icon="question-circle-linear"
              className="!h-11 !text-sm md:!h-10 md:!w-auto md:!px-3.5 md:!text-[13px] md:shrink-0 md:whitespace-nowrap"
              onClick={() => {
                setHowItWorksOpen(true);
                analyticsService.pushEvent("preauth_info_opened", analyticsParams);
              }}
            >
              {t("payment.howItWorks")}
            </Button>
          </div>

          <div className="mt-6 mb-2 flex items-center justify-between">
            <span className="text-sm font-semibold">{t("payment.yourCard")}</span>
            <span className="flex items-center gap-1 text-xs font-semibold text-qv-green">
              <Icon name="lock-keyhole-minimalistic-bold" size={14} />
              {t("payment.secure")}
            </span>
          </div>

          <div className="flex flex-col gap-3">
            <Field label={t("payment.cardNumber")} error={fieldErrors.number} trailing={<CardBrands />}>
              <div className="pt-1">
                <CardNumberElement
                  options={{ style: ELEMENT_STYLE, placeholder: "1234 1234 1234 1234", showIcon: false }}
                  onChange={onFieldChange("number")}
                />
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("payment.expiry")} error={fieldErrors.expiry}>
                <div className="pt-1">
                  <CardExpiryElement options={{ style: ELEMENT_STYLE }} onChange={onFieldChange("expiry")} />
                </div>
              </Field>
              <Field label={t("payment.cvc")} error={fieldErrors.cvc}>
                <div className="pt-1">
                  <CardCvcElement options={{ style: ELEMENT_STYLE }} onChange={onFieldChange("cvc")} />
                </div>
              </Field>
            </div>
          </div>
          <p className="mt-2 text-xs leading-[18px] font-medium text-qv-muted">{t("payment.acceptedCards")}</p>

          {formError && (
            <div className="mt-6 rounded-xl bg-qv-red-50 px-4 py-3.5 flex gap-2.5 items-start" role="alert">
              <Icon name="danger-circle-linear" size={20} className="text-qv-red" />
              <p className="text-sm leading-5 font-medium text-qv-red">{formError}</p>
            </div>
          )}
        </ScreenLayout>
      </div>

      <HowItWorksSheet open={howItWorksOpen} onClose={() => setHowItWorksOpen(false)} />

      <InvoiceSheet
        open={invoiceOpen}
        email={email}
        onSave={(details) => {
          analyticsService.pushEvent("invoice_details_saved", { ...analyticsParams, context: "payment" });
          goToCharging(details);
        }}
        onSkip={() => goToCharging(null)}
        onClose={() => setInvoiceOpen(false)}
      />
    </>
  );
};

const StripeForm = () => {
  const { i18n } = useTranslation();

  return (
    <Elements stripe={stripePromise} options={{ locale: i18n.language || "fr", fonts: STRIPE_FONTS }}>
      <CheckoutForm />
    </Elements>
  );
};

export default StripeForm;
